/**
 * Where the SPA is served from — the single source of truth for every absolute
 * app URL the client builds.
 *
 * Catalyst web client hosting is *mounted* at `/app/` (gateway rule
 * `/app/{path}` → client, see `catalyst-user-rules.json`), but the URLs users see
 * and share are root-relative: `https://app.nibni.com/properties/<id>`. An API
 * Gateway rule rewrites root paths onto the client mount, so the mount point and
 * the public base are two different things:
 *
 *   APP_BASE_PATH  ''        the public base — what goes in links, and what the
 *                            Angular router treats as its root (`baseHref: /`)
 *   CLIENT_MOUNT   '/app/'   where Catalyst actually serves the files from; still
 *                            used for assets (`deployUrl` in angular.json) and by
 *                            `404.html` to fetch the shell
 *
 * To move the public base back under the Catalyst mount, set `APP_BASE_PATH` to
 * `'/app'` and `baseHref` in `angular.json` to `'/app/'` — nothing else changes,
 * because every emitted link goes through the helpers below.
 *
 * Legacy `/app/...` URLs already sitting in inboxes and push payloads keep
 * working: `main.ts` strips the prefix before the router attaches.
 */

// Explicitly `string`, not the literal type: these are configuration knobs meant
// to be flipped, and a literal type makes every comparison against the other one
// a TS2367 "no overlap" error the moment they differ.
/** Public base path for app URLs. Empty string = site root. */
export const APP_BASE_PATH: string = '';

/** Path Catalyst web client hosting is mounted at. Assets are served from here. */
export const CLIENT_MOUNT_PATH: string = '/app';

/** `/properties/x` → `/properties/x` (or `/app/properties/x` if the base moves). */
export function appPath(route: string): string {
    const raw = String(route || '').trim();
    const normalized = raw ? (raw.startsWith('/') ? raw : `/${raw}`) : '/';
    return `${APP_BASE_PATH}${normalized}`;
}

/**
 * Absolute app URL for sharing. Forces `https` off localhost so a copied link is
 * never an `http://` one, matching what the share/copy actions elsewhere do.
 */
export function appUrl(route: string): string {
    return `${appOrigin()}${appPath(route)}`;
}

/** Current origin, upgraded to https unless we are on a local dev host. */
export function appOrigin(): string {
    if (typeof window === 'undefined') return '';
    try {
        const u = new URL(window.location.origin);
        const host = u.hostname.toLowerCase();
        const isLocal = host === 'localhost' || host.startsWith('127.') || host.startsWith('0.0.0.0');
        if (!isLocal && u.protocol !== 'https:') u.protocol = 'https:';
        return u.origin;
    } catch {
        return window.location.origin || '';
    }
}

/**
 * Same-origin URLs to try for a bundled asset, most likely first.
 *
 * `deployUrl: '/app/'` puts built assets under the Catalyst mount, while
 * `ng serve` serves them from the root — and nothing readable at runtime says
 * which one is in play. An `<img src>` doesn't care (either 404s harmlessly), but
 * `fetch()` does, so callers that need the *bytes* walk this list.
 */
export function assetUrlCandidates(assetPath: string): string[] {
    const normalized = String(assetPath || '').trim().replace(/^\/+/, '');
    if (!normalized) return [];
    return [`${CLIENT_MOUNT_PATH}/${normalized}`, `/${normalized}`];
}

/**
 * Strip the Catalyst mount prefix (and the public base, when they differ) from a
 * pathname, yielding the route as the Angular router sees it. Accepts both the
 * current root-relative shape and the legacy `/app/...` one.
 */
export function stripAppBase(pathname: string): string {
    let path = String(pathname || '');
    for (const prefix of [CLIENT_MOUNT_PATH, APP_BASE_PATH]) {
        if (!prefix) continue;
        if (path === prefix) return '/';
        if (path.startsWith(`${prefix}/`)) path = path.slice(prefix.length);
    }
    return path || '/';
}
